import React from 'react';
import { usePermissions } from '../contexts/PermissionContext';

const PermissionButton = ({ 
  permissions, 
  requireAll = false, 
  hideIfDenied = false,
  disabled = false,
  className = '',
  children,
  onClick,
  variant = 'default', // 'default', 'icon'
  ...rest
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();
  
  // Determine if user has the required permissions
  let hasAccess = true;
  
  if (permissions) {
    if (typeof permissions === 'string') {
      hasAccess = hasPermission(permissions);
    } else if (Array.isArray(permissions)) {
      hasAccess = requireAll 
        ? hasAllPermissions(permissions) 
        : hasAnyPermission(permissions);
    }
  }
  
  // If access denied and we should hide the button
  if (!hasAccess && hideIfDenied) {
    return null;
  }
  
  // Default button styles based on variant
  let baseClassName;
  let permissionClassName;
  
  if (variant === 'icon' || className.includes('p-0') || className.includes('bg-transparent')) {
    // Icon button styling
    baseClassName = "inline-flex items-center focus:outline-none";
    permissionClassName = hasAccess 
      ? "" // Keep original colors from className
      : "text-gray-300 cursor-not-allowed hover:text-gray-300"; // Grey out
  } else {
    // Regular button styling
    baseClassName = "inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2";
    permissionClassName = hasAccess 
      ? "bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500" 
      : "bg-gray-300 text-gray-500 cursor-not-allowed";
  }
  
  // Combine class names, prioritizing permission-based styling
  const finalClassName = hasAccess 
    ? `${baseClassName} ${className}` 
    : `${baseClassName} ${permissionClassName}`;
  
  // Handle click only if user has access and button is not disabled
  const handleClick = (e) => {
    if (!hasAccess || disabled) {
      e.preventDefault();
      return;
    }
    
    if (onClick) {
      onClick(e);
    }
  };
  
  return (
    <button
      className={finalClassName}
      onClick={handleClick}
      disabled={!hasAccess || disabled}
      title={!hasAccess ? "You don't have permission to perform this action" : undefined}
      {...rest}
    >
      {children}
    </button>
  );
};

export default PermissionButton; 